import { cn } from '@/utils';
import { COMMON_TEXT, SIMULATION_LABELS } from '@/constants';
import { ContentCarousel, ModalHeader } from './subcomponents';
import { getStepStatus, calculateActiveCardIndex } from './utils';
import { MODAL_BODY_HORIZONTAL_PADDING_REM } from './types';
import { SimulationProgressNav } from '@/components/Simulations';
import { SlideoutModal } from '@/components/Modals';
import React, { useMemo } from 'react';
import type { SimulationScenarioSlideoutModalProps } from './types';

/**
 * SimulationScenarioSlideoutModal
 *
 * Orchestrates the pre-call planning modal experience:
 * 1. Computes which card is currently in focus.
 * 2. Derives progress-nav steps from the planning cards array.
 * 3. Renders the progress navigation, modal header, and animated content carousel.
 *
 * This component intentionally delegates heavy UI rendering to subcomponents so this
 * file can stay focused on state derivation and wiring.
 */
export const SimulationScenarioSlideoutModal: React.FC<SimulationScenarioSlideoutModalProps> = ({
  activeCard,
  animatingStepId,
  isOpen,
  onClose,
  onComplete,
  onNext,
  onPrevious,
  planningCards = [],
}) => {
  // Resolve the active carousel index once per dependency change.
  // This index controls which card is translated into the viewport.
  const normalizedActiveCardIndex = useMemo(() => {
    return calculateActiveCardIndex(planningCards, activeCard);
  }, [activeCard, planningCards]);

  // Normalize card data into the step shape consumed by SimulationProgressNav.
  // Status is computed relative to the active card, producing completed/active/upcoming.
  const steps = useMemo(() => {
    return planningCards?.map((card) => ({
      id: card.stepNumber,
      label: `${SIMULATION_LABELS.STEP} ${card.stepNumber}`,
      status: getStepStatus(card.stepNumber, activeCard),
    }));
  }, [activeCard, planningCards]);

  return (
    <SlideoutModal
      isOpen={isOpen}
      onClose={onClose}
      title={COMMON_TEXT.PRECALL_PLANNING}
      closeButtonIconOnly={false}
      className={cn('opacity-1 transition-opacity delay-150 duration-200', {
        'pointer-events-none opacity-0 delay-0 duration-0': !isOpen,
      })}
    >
      {/* ================================ */}
      {/* Progress Navigation
          - Displays step progression and allows next/previous navigation.
          - Uses derived `steps` array for status-aware labels and interactions.
      */}
      {/* ================================ */}
      <div className="mx-auto flex w-full items-center justify-center px-6 pb-7 pt-10">
        <SimulationProgressNav
          activeStep={activeCard?.stepNumber}
          animatingStepId={animatingStepId}
          disableNext={false}
          disablePrevious={false}
          iconAnimationType="pop"
          onComplete={onComplete}
          onNext={onNext}
          onPrevious={onPrevious}
          steps={steps}
        />
      </div>

      {/* ================================ */}
      {/* Modal Header
          - Displays active step icon, step number, and active card title.
          - Kept as a separate subcomponent to isolate typography/icon presentation.
        */}
      {/* ================================ */}
      <ModalHeader activeCard={activeCard} />

      {/* ================================ */}
      {/* Content Carousel
          - Handles horizontal slide animation between planning cards.
          - Receives precomputed active index and spacing constants from this component.
          - Card content rendering is delegated to subcomponents for simpler maintenance.
        */}
      {/* ================================ */}
      <ContentCarousel
        activeCard={activeCard}
        isOpen={isOpen}
        normalizedActiveCardIndex={normalizedActiveCardIndex}
        horizontalPaddingRem={MODAL_BODY_HORIZONTAL_PADDING_REM}
        planningCards={planningCards}
      />
    </SlideoutModal>
  );
};

SimulationScenarioSlideoutModal.displayName = 'SimulationScenarioSlideoutModal';

export default SimulationScenarioSlideoutModal;
